import { intl } from '@fullstory/intl';
import {
  Dimension,
  DimensionSettings,
  SortOrder,
  SortType,
  UniqueAggregation,
} from '@fullstory/proto/fs/api/metrics';
import {
  Constraints,
  EventSequence,
  TimeInterval,
  TimeRange,
} from '@fullstory/proto/fs/api/segment';
import {
  FunnelOrientation,
  UnnamedFunnel,
  UnnamedOverlay,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  Color,
  SegmentSettings,
  TimeRangeComparison,
  TrendSteps,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { fullstorySelectors, useSavedFunnelsActions } from '@fullstory/redux';
import {
  FUNNEL_CREATE_WITH_HASH_ROUTE,
  FUNNEL_DETAILS_WITH_HASH_ROUTE,
  generateRoutePath,
} from '@fullstory/routes';
import { isEqual } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_FUNNEL_CONSTRAINTS, MIN_FUNNEL_EVENT_FILTERS } from '../constants';
import { FunnelFormAggregation, FunnelFormType } from '../types/funnels';
import { DEFAULT_SEGMENT, getAnyActivityEvent } from './constants';
import { getDefaultTimeRange } from './timeRange';
import { overrideEventDeps } from './unnamedOverlays';
import { int64 } from '@fullstory/proto/google/protobuf';
import { FunnelRecord } from '@fullstory/proto/fs/services/search-settings';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

const ANY_ACTIVITY_EVENT = getAnyActivityEvent();

export const generateFunnel = (
  events: EventSequence,
  constraints: Constraints | null,
  segmentName: string,
  aggregation: FunnelFormAggregation = UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
): FunnelFormType => {
  const defaultFunnel = getDefaultFunnel();
  return {
    ...defaultFunnel,
    name: intl.formatMessage(
      { defaultMessage: '{segmentName} Event Funnel', id: 'Xc8viR' },
      { segmentName },
    ),
    events: {
      ...defaultFunnel.events,
      ...events,
      constraints: {
        ...defaultFunnel.events.constraints!,
        ...constraints,
        // all funnels must be in order
        inOrder: true,
      },
    },
    aggregation,
  };
};

export const getDefaultFunnel = (
  isWeeklyTimeRange: boolean = false,
  overlay?: UnnamedOverlay | null,
): FunnelFormType => ({
  id: '',
  name: '',
  description: '',
  events: {
    events: Array(MIN_FUNNEL_EVENT_FILTERS).fill(
      overrideEventDeps(ANY_ACTIVITY_EVENT, overlay?.deps),
    ),
    constraints: DEFAULT_FUNNEL_CONSTRAINTS,
  },
  archived: false,
  disabledEvents: [],
  segment: overlay?.segments && overlay.segments.length ? overlay.segments : [DEFAULT_SEGMENT],
  timeRange: overlay?.timeRange ?? getDefaultTimeRange(isWeeklyTimeRange),
  timeRangeComparison: null,
  dimension: null,
  dimensionApplication: null,
  orientation: FunnelOrientation.FUNNEL_ORIENTATION_HORIZONTAL,
  timeInterval: TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
  trendSteps: null,
  aggregation: UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
  visibility: Visibility.UNSET,
});

export interface UnnamedFunnelFormValues {
  funnel: FunnelFormType;
  segments: SegmentSettings[];
  timeRange: TimeRange | null;
  timeRangeComparison: TimeRangeComparison | null;
  orientation: FunnelOrientation;
  dimensions: Dimension[];
  unnamedFunnel?: UnnamedFunnel | null;
  funnelId: string | null;
  disabledEvents: number[];
  timeInterval: TimeInterval;
  trendSteps: TrendSteps | null;
}

const { getOrgId } = fullstorySelectors;

export function uniqueAggregationToFunnelFormAggregation(
  aggregation: UniqueAggregation | undefined | null,
): FunnelFormAggregation {
  // funnels only supports two types of aggregation right now
  return aggregation === UniqueAggregation.UNIQUE_AGGREGATION_SESSION
    ? UniqueAggregation.UNIQUE_AGGREGATION_SESSION
    : UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL;
}

export function getFunnelUserOrSessionText(aggregation: FunnelFormAggregation) {
  return aggregation === UniqueAggregation.UNIQUE_AGGREGATION_SESSION
    ? intl.formatMessage({ defaultMessage: 'Sessions', id: 'Z3EDd9' })
    : intl.formatMessage({ defaultMessage: 'Users', id: 'YDMrKK' });
}

export const getDefaultFunnelFormFromUnnamedFunnel = (
  unnamedFunnel: UnnamedFunnel,
  visibility: Visibility,
): FunnelFormType => {
  const defaultFunnel = getDefaultFunnel();
  const eventSequence: EventSequence = {
    ...defaultFunnel.events,
    ...unnamedFunnel.funnel?.events,
    constraints: {
      ...defaultFunnel.events.constraints!,
      ...unnamedFunnel.funnel?.events?.constraints,
      // all funnels must be in order
      inOrder: true,
    },
  };

  return {
    id: unnamedFunnel.funnel?.id ?? '',
    name: unnamedFunnel.funnel?.name ?? '',
    description: unnamedFunnel.funnel?.description ?? '',
    archived: false,
    events: eventSequence,
    disabledEvents: unnamedFunnel.disabledEvents,
    segment: unnamedFunnel.segments,
    timeRange: unnamedFunnel.timeRange,
    timeRangeComparison: unnamedFunnel.timeRangeComparison,
    dimension: unnamedFunnel.dimensionSettings,
    dimensionApplication: unnamedFunnel.funnel?.funnelSettings?.dimensionApplication ?? null,
    orientation: unnamedFunnel.orientation,
    timeInterval: unnamedFunnel.timeInterval,
    trendSteps: unnamedFunnel.funnel?.funnelSettings?.trendSteps ?? null,
    aggregation: uniqueAggregationToFunnelFormAggregation(unnamedFunnel.funnel?.aggregation),
    visibility,
  };
};

const getFunnelFormFromUnnamedFunnel = (
  funnel: FunnelRecord | null,
  visibility: Visibility,
): FunnelFormType => ({
  id: funnel?.funnel?.id ?? '',
  name: funnel?.funnel?.name ?? '',
  description: funnel?.funnel?.description ?? '',
  archived: funnel?.metadata?.archived ?? false,
  segment: funnel?.funnel?.funnelSettings?.segment ?? null,
  events: funnel?.funnel?.events ?? { constraints: null, events: [] },
  disabledEvents: [],
  dimension: funnel?.funnel?.funnelSettings?.dimensionSettings ?? null,
  dimensionApplication: funnel?.funnel?.funnelSettings?.dimensionApplication ?? null,
  timeRange: funnel?.funnel?.funnelSettings?.timeRange ?? null,
  timeRangeComparison: funnel?.funnel?.funnelSettings?.timeRangeComparison ?? null,
  timeInterval: funnel?.funnel?.funnelSettings?.timeInterval ?? null,
  trendSteps: null,
  orientation: FunnelOrientation.FUNNEL_ORIENTATION_VERTICAL,
  aggregation: uniqueAggregationToFunnelFormAggregation(funnel?.funnel?.aggregation),
  visibility,
});

export const useFunnelFormFromFunnelRecord = (
  funnel: FunnelRecord | null,
  visibility: Visibility,
): FunnelFormType =>
  useMemo(() => getFunnelFormFromUnnamedFunnel(funnel, visibility), [funnel, visibility]);

export const useCreateUnnamedFunnelPath = (
  funnelActions: ReturnType<typeof useSavedFunnelsActions>,
) => {
  const orgId = useSelector(getOrgId);

  return useCallback(
    ({
      funnel: { dimensionApplication, ...funnel },
      segments,
      timeRange,
      timeRangeComparison,
      orientation,
      trendSteps,
      dimensions,
      unnamedFunnel,
      funnelId,
      disabledEvents,
      timeInterval,
    }: UnnamedFunnelFormValues) => {
      const dimensionSettings: DimensionSettings = {
        dimensions,
        dimension: dimensions[0] ?? null,
        sortOrder: SortOrder.SORT_ORDER_ASC,
        sortByDefault: false,
        sortType: SortType.SORT_TYPE_INVALID,
        limit: 0,
        computeTopLevel: false,
        computeNumGroups: false,
        allowAllPartialResults: false,
        noFallback: false,
      };

      // when values change, create new unnamed funnel and push new route hash to the history stack
      const newUnnamedFunnel: UnnamedFunnel = {
        funnel: {
          id: funnel.id,
          name: funnel.name,
          description: funnel.description,
          events: {
            ...funnel.events,
            constraints: {
              ...DEFAULT_FUNNEL_CONSTRAINTS,
              ...funnel.events.constraints,
              timeRange,
            },
          },
          funnelSettings: {
            conversionId: int64.fromNumber(0),
            segmentId: '',
            dateRange: null,
            segment: segments,
            dimensionSettings,
            dimensionApplication,
            timeRange,
            timeRangeComparison,
            color: Color.COLOR_INVALID,
            timeInterval,
            trendSteps,
          },
          aggregation: funnel.aggregation ?? UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
        },
        segments,
        segmentIds: [],
        timeRange,
        timeRangeComparison,
        timeInterval,
        orientation,
        dimensionSettings,
        disabledEvents,
      };
      if (isEqual(unnamedFunnel, newUnnamedFunnel)) return;

      const funnelHash = funnelActions.createUnnamedFunnel(orgId, newUnnamedFunnel);
      return funnelId
        ? generateRoutePath(FUNNEL_DETAILS_WITH_HASH_ROUTE, {
            orgId,
            id: funnelId,
            hash: funnelHash,
          })
        : generateRoutePath(FUNNEL_CREATE_WITH_HASH_ROUTE, {
            orgId,
            hash: funnelHash,
          });
    },
    [funnelActions, orgId],
  );
};
